import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Setlist, SetlistSong } from '../types/database';

export function useBandSetlists(bandId: string | undefined, includePrivate = false) {
  const [setlists, setSetlists] = useState<Setlist[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSetlists = useCallback(async () => {
    if (!bandId) return;

    let query = supabase
      .from('setlists')
      .select('*, songs:setlist_songs(*)')
      .eq('band_id', bandId)
      .order('created_at', { ascending: false });

    if (!includePrivate) {
      query = query.eq('is_public', true);
    }

    const { data } = await query;
    setSetlists((data as Setlist[]) ?? []);
    setLoading(false);
  }, [bandId, includePrivate]);

  useEffect(() => {
    fetchSetlists();
  }, [fetchSetlists]);

  return { setlists, loading, refetch: fetchSetlists };
}

export async function createSetlist(
  bandId: string,
  title: string,
  description: string,
  isPublic: boolean,
  songs: { title: string; artist: string; duration_seconds: number | null; notes: string }[],
  createdBy: string
): Promise<{ error: string | null }> {
  const { data: setlist, error: setlistError } = await supabase
    .from('setlists')
    .insert({
      band_id: bandId,
      title,
      description,
      is_public: isPublic,
      created_by: createdBy,
    })
    .select()
    .single();

  if (setlistError) return { error: setlistError.message };

  if (songs.length > 0) {
    const { error: songsError } = await supabase.from('setlist_songs').insert(
      songs.map((song, idx) => ({
        setlist_id: setlist.id,
        title: song.title,
        artist: song.artist,
        duration_seconds: song.duration_seconds,
        notes: song.notes,
        position: idx,
      }))
    );
    if (songsError) return { error: songsError.message };
  }

  return { error: null };
}

export async function deleteSetlist(setlistId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('setlists').delete().eq('id', setlistId);
  return { error: error?.message ?? null };
}

export async function updateSetlistSongs(
  setlistId: string,
  songs: Omit<SetlistSong, 'id' | 'setlist_id' | 'created_at'>[]
): Promise<{ error: string | null }> {
  // Delete existing songs
  await supabase.from('setlist_songs').delete().eq('setlist_id', setlistId);

  if (songs.length > 0) {
    const { error } = await supabase.from('setlist_songs').insert(
      songs.map((song, idx) => ({
        setlist_id: setlistId,
        title: song.title,
        artist: song.artist,
        duration_seconds: song.duration_seconds,
        notes: song.notes,
        position: idx,
      }))
    );
    if (error) return { error: error.message };
  }

  return { error: null };
}
